/**
 * Shared usage-metering contracts.
 *
 * The router intentionally keeps three separate views of one upstream response:
 *
 * - protocol/raw usage: what the upstream actually returned;
 * - billing usage: dimensions that can affect tenant charge or upstream cost;
 * - display usage: the legacy four buckets shown to tenants by default.
 *
 * All shapes below are JSON-serialisable and can be persisted verbatim in SQL
 * TEXT columns.
 */
import { usdMicrosToCentsFloor } from './money.ts';

/**
 * `openai_images_v1` meters a built-in `image_generation` tool call, whose
 * tokens the upstream reports in `tool_usage.image_gen` rather than in `usage`.
 * Those image tokens bill as `output_tokens` at the image model's output
 * rate. The request's input is a PARTITION of `input_tokens`: reference-image
 * tokens (`input_tokens_details.image_tokens`) bill at
 * `image_input_per_mtok_usd` when that rate is configured, and the remainder
 * bills at `input_per_mtok_usd`. A NULL image-input rate falls back to the
 * text input rate so every existing SKU keeps today's cents. The text tokens
 * the Responses tool path spends translating the prompt are procurement
 * overhead and are not charged on. The input side is billed rather than
 * absorbed for a blunt reason: the caller chooses how large it is, so
 * absorbing it would be an unbounded liability, not an overhead.
 *
 * A response under this profile that produced no image is free — about one
 * upstream call in seven answers 200 with text instead of generating, and a
 * caller who asked for an image and got none has received nothing to pay for.
 *
 * `imageRequested` must be a fact about the INGRESS, never about the body.
 * Both of the obvious alternatives are holes, in opposite directions: deciding
 * from the response alone makes every plain text request to an image SKU free,
 * and deciding from a `tools` array the tenant writes lets a caller declare
 * their own text request free — attach the tool to a long prompt, tell the
 * model not to draw, and the router pays for the whole turn at zero. Only an
 * ingress whose codec forces the tool makes "asked for an image" unforgeable.
 *
 * It is also the authorization signal for the image lane: a SKU may reach
 * image generation only when its pricing row carries this profile, so
 * "produces images" and "has an image price" cannot come apart.
 *
 * ── `ark_video_token_v1` (#1900, parent #1838 sub-items D/F) ────────────────────
 *
 * The Ark task API's profile: an ASYNCHRONOUS generation whose billable count
 * exists only on the terminal poll. It is a TOKEN-lane kind — deliberately
 * absent from {@link UnitMeteredProfileKind} below — under the owner's
 * lane-selection ruling of 2026-08-13 (ADR-012 §4, *Lane-selection
 * corollary*, which supersedes that ADR's *Media corollary*).
 *
 * NAMING RULE (owner ruling, 2026-08-16): a profile name is protocol family +
 * billing basis — what shape it reads AND what it bills in — the way
 * `duration_seconds_v1` and `openai_images_unit_v1` already carry their basis
 * in the name. This kind was born `ark_tasks_v1`, which named only the
 * protocol and hid that it bills tokens; it was renamed to
 * `ark_video_token_v1` on 2026-08-16, while the value had ZERO stored rows in
 * dev, staging and production (measured) and its migration was unapplied in
 * every environment, so the rename is a pure code + unapplied-migration edit,
 * not a data migration.
 *
 * TWO PROPERTIES THAT ARE MONEY, both measured (#1874 §1.1) rather than
 * assumed, and both enforced elsewhere — this type only names the kind:
 *
 *  1. A `usage` block is present from the FIRST poll with all counts zero, so
 *     metering any poll that is not terminal delivers the video and bills
 *     nothing. The refusal is `extractUsage`'s phase argument in
 *     `egress-runtime/src/upstream-adapter.ts`, not a rule here.
 *  2. `usage.duration` on that block is the REQUEST ECHOED BACK, not a
 *     measurement (#1838 ADR 2), so it is not a quantity anything may price
 *     on even though it is the only non-zero number a pending poll carries.
 *
 * A SKU on this kind is servable only with BOTH per-SKU documents this round
 * adds beside the metering spec — the parameter surface and the admission
 * surface (`ark-task-parameters.ts`, `ark-task-admission.ts`). Neither is
 * optional and neither has a default: see ADR-013 for why a missing admission
 * document must refuse rather than fall back.
 */
export type MeteringProfileKind =
  | 'anthropic_messages_v2'
  | 'openai_responses_v2'
  | 'openai_images_v1'
  | 'gemini_generate_content_v1'
  | 'openai_chat_v1'
  | 'legacy_4_bucket'
  | 'duration_seconds_v1'
  | 'text_characters_v1'
  | 'flat_call_v1'
  | 'openai_images_unit_v1'
  | 'ark_video_token_v1'
  | 'ark_video_token_tiered_v1'
  | 'ark_video_seconds_v1'
  | 'ark_video_seconds_ref_v1'
  | 'grok_video_seconds_v1'
  | 'grok_image_unit_v1';

export const KNOWN_METERING_PROFILE_KINDS: readonly MeteringProfileKind[] = [
  'anthropic_messages_v2',
  'openai_responses_v2',
  'openai_images_v1',
  'gemini_generate_content_v1',
  'openai_chat_v1',
  'legacy_4_bucket',
  'duration_seconds_v1',
  'text_characters_v1',
  'flat_call_v1',
  'openai_images_unit_v1',
  'ark_video_token_v1',
  'ark_video_token_tiered_v1',
  'ark_video_seconds_v1',
  'ark_video_seconds_ref_v1',
  'grok_video_seconds_v1',
  'grok_image_unit_v1',
] as const;

export function isMeteringProfileKind(value: unknown): value is MeteringProfileKind {
  return (
    typeof value === 'string' &&
    (KNOWN_METERING_PROFILE_KINDS as readonly string[]).includes(value)
  );
}

/**
 * Profiles billed by counted UNITS (`unit_usage`) instead of token
 * dimensions (#1650). A profile in this set never reads token counters, never
 * consults `selectPricingTier`, and prices exclusively off
 * `MeteredPricingRates.unit_rates`. Token profiles symmetrically ignore
 * `unit_usage` entirely.
 *
 * The profile kind does NOT constrain which `unit` values an event may carry:
 * the (unit, variant_key) price table is the billing authority, and an event
 * whose unit has no price row lands on `unit_price_missing` rather than on a
 * per-profile allowlist.
 *
 * `openai_images_unit_v1` (#1668) is the per-image sibling of the token
 * profile `openai_images_v1`: it bills `unit: 'image'` at the count of images
 * the upstream actually DELIVERED (the gateway's `tool_usage.image_gen`
 * observation), so a soft refusal — 200 with prose, no image — is the
 * whitelisted `unit_not_delivered` zero rather than a token charge. The two
 * profiles coexist deliberately: the same physical model can carry one SKU on
 * each, and the kind on the SKU's pricing row decides which branch prices it.
 *
 * NAMING (#1839): a unit-lane kind is named after the UNIT IT BILLS, never
 * after the protocol the request arrived on. This lane is upstream-agnostic by
 * construction — `(unit, variant_key)` is the whole pricing key and
 * `lookupUnitRate` never sees a protocol — so a protocol-derived name would
 * force an N x N naming explosion the moment a second ingress sells seconds.
 * `duration_seconds_v1` and `text_characters_v1` are that rule applied; the
 * pre-existing `flat_call_v1` already followed it. `openai_images_unit_v1` is
 * the one violation, frozen rather than renamed because it has settled billing
 * history. The extractor may still dispatch on protocol — reading a response
 * is protocol work — but the profile's NAME must not.
 *
 * ── AND THEN THE 2026-08-16 NAMING RULING, WHICH IS THE ONE THAT GOVERNS ──
 *
 * The owner's later ruling (recorded on `ark_video_token_v1` above) names a
 * profile as SALES FORM: protocol family + billing basis, because a profile
 * is a fixed sales shape — its tier axes included — not a bare lane. The two
 * rules collide exactly once, on `ark_video_seconds_v1`: it is a UNIT-lane
 * kind whose name carries the ark task family, deliberately, because it is a
 * DIFFERENT sales form from `duration_seconds_v1` — same unit (`second`),
 * different tier matrix (`{has_video_input}-{resolution}` vs `{resolution}`)
 * — and under the sales-form model a new tier scheme IS a new profile kind
 * (`PROFILE_METERING_CAPABILITY`'s own column note says so). #1839's rule
 * survives where it was made: `duration_seconds_v1` stays the protocol-
 * agnostic per-second lane and is NOT renamed toward any vendor; the
 * sales-form ruling governs new forms.
 */
export type UnitMeteredProfileKind =
  | 'duration_seconds_v1'
  | 'text_characters_v1'
  | 'flat_call_v1'
  | 'openai_images_unit_v1'
  | 'ark_video_seconds_v1'
  | 'ark_video_seconds_ref_v1'
  | 'grok_video_seconds_v1'
  | 'grok_image_unit_v1';

export const UNIT_METERED_PROFILE_KINDS: readonly UnitMeteredProfileKind[] = [
  'duration_seconds_v1',
  'text_characters_v1',
  'flat_call_v1',
  'openai_images_unit_v1',
  'ark_video_seconds_v1',
  'ark_video_seconds_ref_v1',
  'grok_video_seconds_v1',
  'grok_image_unit_v1',
] as const;

export function isUnitMeteredProfileKind(value: unknown): value is UnitMeteredProfileKind {
  return (
    typeof value === 'string' &&
    (UNIT_METERED_PROFILE_KINDS as readonly string[]).includes(value)
  );
}

/**
 * The profiles served by the ASYNCHRONOUS task ingress (`/v1/tasks/*`), and
 * therefore the profiles for which the two per-SKU task documents — the
 * parameter surface and the admission surface — are REQUIRED (ADR-013 §6).
 *
 * A CROSS-CUTTING list rather than a gateway constant, because two seats must
 * agree about it and they live in different workspaces: the gateway's route
 * gate decides whether to serve a submission, and the platform write face
 * decides whether the two documents may be stored. If the write face were
 * narrower, a SKU the ingress serves could not be configured at all; if it were
 * wider, an operator could attach a concurrency allowance to a SKU no gate
 * reads it for.
 *
 * `duration_seconds_v1` is here because it is a LANE, not an ingress
 * (ADR-012 §4's *Naming corollary* forbids the unit lane's profile names from
 * naming a protocol, an upstream or an ingress, so there is deliberately no
 * `ark_task_seconds_v1` to put here instead). The set says which profiles the
 * task ingress serves; where the money comes from is settlement's question.
 *
 * `ark_video_seconds_v1` is here because it is a SALES FORM of this wire
 * (owner naming ruling, 2026-08-16 — see `UnitMeteredProfileKind`): per-second
 * billing on the `{has_video_input}-{resolution}` matrix, served by the same
 * submit/poll lifecycle. It does not replace `duration_seconds_v1`; the two
 * are different tier schemes an operator chooses between at registration.
 *
 * `ark_video_token_tiered_v1` is the token-lane sibling of that matrix
 * (2026-08-30): per-token on `{has_video_input}-{resolution}`. It does not
 * replace `ark_video_token_v1`; live SKUs stay on the two-tier video-input
 * form until an operator cuts them over. Silently widening
 * `ark_video_token_v1`'s template would mismatch every existing
 * `novideoinput` / `includesvideoinput` price row.
 *
 * `ark_video_seconds_ref_v1` is the MiniMax A sales form (2026-09-02):
 * per-second on `{resolution}` plus a reference-image surcharge. It does
 * not replace `duration_seconds_v1`; that form has no surcharge seat, and
 * freezing a count onto it would quarantine Seedance image-to-video.
 *
 * Membership is not a claim that a SKU can be METERED — that stays data, and
 * the route gate answers it separately from the SKU's stored spec and price
 * rows.
 */
export const ARK_TASK_INGRESS_PROFILE_KINDS: readonly MeteringProfileKind[] = [
  'ark_video_token_v1',
  'ark_video_token_tiered_v1',
  'duration_seconds_v1',
  'ark_video_seconds_v1',
  'ark_video_seconds_ref_v1',
] as const;

export function isArkTaskIngressProfileKind(value: unknown): value is MeteringProfileKind {
  return (
    typeof value === 'string' &&
    (ARK_TASK_INGRESS_PROFILE_KINDS as readonly string[]).includes(value)
  );
}

/**
 * The profiles served by the Grok video ingress (`/v1/videos/*`), sibling of
 * {@link ARK_TASK_INGRESS_PROFILE_KINDS} (#1949 B / #2138).
 *
 * TWO SEATS, ONE LIST, held equal by test: the grok route gate decides
 * whether to serve a submission, and the platform write face decides whether
 * the admission document may be stored. Do NOT put these kinds in
 * `ARK_TASK_INGRESS_PROFILE_KINDS` — that list is the `/v1/tasks/*` ingress.
 * `duration_seconds_v1` stays on `ark_video_v1` only; Grok's `{resolution}`
 * matrix with no `has_video_input` is this kind.
 */
export const GROK_VIDEO_INGRESS_PROFILE_KINDS: readonly MeteringProfileKind[] = [
  'grok_video_seconds_v1',
] as const;

export function isGrokVideoIngressProfileKind(value: unknown): value is MeteringProfileKind {
  return (
    typeof value === 'string' &&
    (GROK_VIDEO_INGRESS_PROFILE_KINDS as readonly string[]).includes(value)
  );
}

/**
 * The profiles served by the Grok still-image path on `/v1/images/generations`
 * (#2190). Sibling of {@link GROK_VIDEO_INGRESS_PROFILE_KINDS}. Do NOT put
 * these kinds in `ARK_TASK_INGRESS_PROFILE_KINDS` — that list is the
 * `/v1/tasks/*` ingress. Do NOT put them on the video list: images are
 * synchronous and bill `unit: 'image'`.
 */
export const GROK_IMAGE_INGRESS_PROFILE_KINDS: readonly MeteringProfileKind[] = [
  'grok_image_unit_v1',
] as const;

export function isGrokImageIngressProfileKind(value: unknown): value is MeteringProfileKind {
  return (
    typeof value === 'string' &&
    (GROK_IMAGE_INGRESS_PROFILE_KINDS as readonly string[]).includes(value)
  );
}

export interface MeteringProfileRecord {
  vendor: string;
  model: string;
  protocol: string;
  profile_kind: MeteringProfileKind;
  billable_dimensions_json: string | null;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface UsageDisplay {
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_write_tokens: number;
}

export interface BillingUsageDimensions {
  input_tokens: number;
  output_tokens: number;
  cache_read_input_tokens: number;
  cache_creation_input_tokens: number;
  cache_creation_ephemeral_5m_input_tokens: number;
  cache_creation_ephemeral_1h_input_tokens: number;
  cache_creation_other_input_tokens: number;
  reasoning_tokens: number;
  /**
   * Reference-image (and other image-input) tokens reported in
   * `input_tokens_details.image_tokens`. A subset of `input_tokens`, never an
   * extra counter. Absent on every event stored before #2004; absence means
   * 0, which is "bill the whole input at the text rate" — today's cents.
   */
  image_input_tokens?: number;
}

export interface UsageDetailsAudit {
  service_tier?: string | null;
  inference_geo?: string | null;
  iterations?: number | null;
  server_tool_use?: boolean | null;
}

export interface UsageDetailsSegment {
  profile: MeteringProfileKind;
  display: UsageDisplay;
  billable: BillingUsageDimensions;
  audit?: UsageDetailsAudit;
}

/**
 * Why the router answered a request itself instead of forwarding it
 * (router#1609).
 *
 * Present only on rows the cc-lane admission blacklist intercepted, on all
 * three of its actions. It rides in `usage_details_json` — an additive
 * optional field, like `audit` and `segments` beside it, so `schema_version`
 * stays 1 — because that column is what the operator request surfaces already
 * return per request. Without it, "which rule refused this" is only
 * answerable by hand-querying `request_lifecycle_events`.
 *
 * Whether rating sees it depends on the action, so do not assume either way:
 *
 * - `model-unavailable` and `reject` settle inside
 *   `messages-interception-response.ts`, which writes the `usage_logs` row
 *   directly and never calls `insertUsageEvent`. No metering event exists, so
 *   nothing in `rate-events.ts` can read the block.
 * - `synthesize-200` resolves to a normal 200 and settles through
 *   `settlement.ts`, whose metering arm writes `usage_events` unconditionally.
 *   `rate-events.ts` therefore DOES read this block back, through
 *   `parseUsageDetails`, and hands it to `computeCostBreakdownUsdMicros`
 *   along with the rest of `UsageDetails`.
 *
 * That read is harmless and is meant to stay harmless: nothing branches on
 * `admission`, and the amount is zero because the token counts are zero, not
 * because rating recognises an interception. The event rates to
 * `rating_outcome: 'zero_cost'` on the ordinary `costUsdMicros <= 0` arm, the
 * same one a genuinely free request takes.
 *
 * ── Why the identity fields are here (router#1689) ─────────────────────────
 *
 * The block first shipped carrying the verdict alone — which rule, which
 * action. During #1663 triage that turned out to answer half the question: the
 * row proved THAT `bareApiOnCcLane` refused a request, but not WHAT the
 * classifier believed the sender was, and the rule is a predicate over exactly
 * that. Reconstructing it meant replaying captured traffic against the live
 * classifier, days after the fact and against a configuration that had since
 * moved. `client_kind`, `client_entrypoint` and `lane` are the three inputs
 * the decision was keyed on, so a single `usage_logs` row now answers it.
 *
 * All three are optional because rows written before #1689 do not carry them;
 * absence means "historic row", never "the classifier had no opinion". A
 * current writer always sets `client_kind` and `lane`, and sets
 * `client_entrypoint` only when the client's user-agent declared one — most
 * populations, including all bare-API traffic, declare none.
 *
 * Typed as `string` rather than as the unions they come from, for the same
 * reason `blacklist_rule` and `blacklist_action` are: this is the shape of a
 * PERSISTED row, so a value retired from the live union must still read back.
 * `client_kind` additionally cannot be `AnthropicClientKind` here —
 * that union lives in `packages/egress-runtime`, which imports this package,
 * so naming it would invert the dependency.
 */
export interface UsageDetailsAdmission {
  /** The blacklist rule key that fired, e.g. `bareApiOnCcLane`. */
  blacklist_rule: string;
  /** The configured action, e.g. `model-unavailable`. */
  blacklist_action: string;
  /** The client class the rule judged, e.g. `anthropic-api` (router#1689). */
  client_kind?: string;
  /** The cc entrypoint the client declared, e.g. `sdk-cli`; absent when none. */
  client_entrypoint?: string;
  /** The egress lane the request had reached, e.g. `claude-code`. */
  lane?: string;
}

/** The closed set of billable unit kinds for unit-metered profiles (#1650). */
export type UnitUsageUnit = 'call' | 'second' | 'character' | 'image';

export const KNOWN_UNIT_USAGE_UNITS: readonly UnitUsageUnit[] = [
  'call',
  'second',
  'character',
  'image',
] as const;

/**
 * Counted-unit usage for unit-metered profiles (#1650). Absent on every token
 * profile; token profiles ignore it even when present, so a mislabeled event
 * cannot double-bill.
 */
export interface UnitUsageDetails {
  unit: UnitUsageUnit;
  /** Billed unit count; must be a non-negative safe integer. */
  quantity: number;
  /** Normalized variant string; `''` means no variant. */
  variant_key: string;
  /** Whether the deliverable actually arrived; `false` absorbs the charge. */
  delivered: boolean;
}

export interface UsageDetails {
  schema_version: 1;
  profile: MeteringProfileKind;
  protocol: string;
  display: UsageDisplay;
  billable: BillingUsageDimensions;
  audit?: UsageDetailsAudit;
  /** Set when an admission rule answered this request (router#1609). */
  admission?: UsageDetailsAdmission;
  /**
   * Gemini generateContent L4 session split (#2310). Present only when that
   * ingress actually bucketed the affinity key. Anthropic/OpenAI record the
   * same fact as a `route_affinity_bucketed` lifecycle mark; Gemini has no
   * lifecycle recorder, so the observation rides this optional block.
   * Rating ignores it (same posture as `admission`).
   */
  affinity?: {
    readonly bucketed: true;
    readonly bucket: number;
    readonly buckets: number;
  };
  /** Counted units for unit-metered profiles (#1650); token profiles ignore it. */
  unit_usage?: UnitUsageDetails;
  /**
   * Optional extra unit terms of a multi-term charge (#2149). A LIST, not
   * a single block, because a persisted shape that starts singular and
   * later grows to N > 1 forces every reader to handle both shapes
   * forever. One Responses request can call `x_search` AND `web_search`
   * (router#2150) — two priced surcharge terms in one event — so N > 1
   * is a real shape, not imagined generality. A list that today has one
   * element costs an array wrapper; that is the reversible side.
   *
   * Do not "simplify" this back to a single object on the grounds that
   * only one producer exists today.
   *
   * ABSENT (not `[]`) is what a producer writes when there is no
   * surcharge — a zero-image Grok job must stay byte-identical to the
   * pre-#2149 document. Readers treat absent and `[]` as the same "no
   * surcharge" answer.
   *
   * Entries must be distinct by `(unit, variant_key)`. A duplicate pair
   * is a malformed document (quarantine), never summed: summing would
   * make "two x_search calls" and "one x_search call recorded twice"
   * indistinguishable.
   *
   * A malformed or unreadable entry on the UNIT lane quarantines the
   * WHOLE event rather than being stripped: stripping would leave
   * `unit_usage` intact and silently undercharge the seconds. That is
   * the opposite of the `unit_usage` strip rule, on purpose. The TOKEN
   * lane is the other way around: rating strips the malformed list,
   * keeps the token body, and records a visible unpriced marker
   * (`unit_surcharges_malformed`). Whole-document rejection there
   * would drop the event onto the legacy four-bucket path and silently
   * reprice the body (#1651 M5).
   *
   * The TOKEN lane (#2150) prices this list when it is present and
   * non-empty, adding each term to the token charge. The gate is the
   * field, never the profile's lane: an event without this block takes
   * the pre-surcharge path byte-for-byte. An unpriced term on that
   * lane is skipped (and recorded on `unpriced_dimensions`); it does
   * not zero the token body. The unit lane still quarantines the
   * whole event — the body there IS the unit charge.
   */
  unit_surcharges?: readonly UnitUsageDetails[];
  /**
   * The TOKEN-lane price tier this request was admitted on (#1937).
   *
   * ── IT IS A REQUEST FACT, WRITTEN BEFORE THE UPSTREAM ANSWERED ────────────
   *
   * The gateway resolves it at the ROUTE GATE from canonical request values
   * (ADR-012 §2 self-evidence) and freezes it, so what arrives here is the
   * tier the request was ADMITTED on — not something recomputed at settlement
   * against a spec or a parameter surface that may have moved since, and
   * emphatically not anything read off the upstream's response.
   *
   * That distinction is the whole of why this field exists rather than
   * platform re-deriving the tier: the upstream's terminal document DOES carry
   * information from which the tier could be inferred (the completion-token
   * count roughly doubles on the video-input tier), and that inference is
   * third-party evidence. ADR-012 §2's priced-TIER row admits self-evidence
   * only. Equal is not the test; provenance is.
   *
   * ABSENT MEANS `''`, the base row, which is every token event ever stored
   * and every token SKU that declares no tiers. Rating reads absence and the
   * empty string identically, so no backfill exists or is needed.
   *
   * The unit lane keeps carrying its own key inside `unit_usage.variant_key`
   * and ignores this one; the two are never both meaningful, because a
   * profile is on exactly one lane.
   */
  pricing_variant_key?: string;
  /** Upstream token counters that were present but were not safe non-negative integers. */
  invalid_dimensions?: readonly string[];
  /** Optional per-response/per-stream summaries for CLI probes and SSE. */
  segments?: UsageDetailsSegment[];
  /** Redacted, schema-limited upstream usage snippets for forensic replay. */
  raw_usage?: readonly Record<string, unknown>[];
}

export interface UsageCostBreakdownDimension {
  dimension: string;
  tokens: number;
  per_mtok_usd: number;
  multiplier: number;
  usd_micros: number;
  usd_cents: number;
}

/**
 * One breakdown row a unit-metered charge produces (#1650). A two-term
 * charge (#2149) emits one row per term; both still `dimension: 'unit'`.
 * Deliberately NOT the token shape: a unit count is not `tokens` and a
 * per-unit price is not `per_mtok_usd`, so lying them into those fields
 * would poison every downstream reader that aggregates token dimensions.
 * Narrow with `'unit' in entry` (or `entry.dimension === 'unit'` after
 * that check).
 */
export interface UnitCostBreakdownDimension {
  dimension: 'unit';
  unit: UnitUsageUnit;
  quantity: number;
  variant_key: string;
  rate_usd_micros_per_unit: number;
  usd_micros: number;
  usd_cents: number;
}

export type UsageCostBreakdownEntry =
  | UsageCostBreakdownDimension
  | UnitCostBreakdownDimension;

export interface UsageCostBreakdown {
  schema_version: 1;
  profile: MeteringProfileKind;
  rate_multiplier: number;
  cost_usd_micros: number;
  cost_usd_cents: number;
  fallback_to_legacy_4_bucket: boolean;
  unpriced_dimensions: readonly string[];
  dimensions: readonly UsageCostBreakdownEntry[];
  /** min_prompt_tokens of the selected pricing tier; 0 for the base tier. */
  pricing_tier_min_prompt_tokens: number;
  /** Normalized prompt-token total the tier selection used. */
  pricing_tier_prompt_tokens: number;
}

/**
 * One TOKEN-lane price tier of a pricing generation (#1937), keyed by the
 * `variant_key` the gateway resolved from canonical request values.
 *
 * Same rate columns as {@link PricingTierRates} and the same "replaces the
 * base row wholesale" semantics, because the axis is the same kind of thing —
 * a different price list for a differently-shaped request. What differs is
 * what selects it: a prompt-length tier is selected by a NUMBER derived from
 * the response's own counters, while this is selected by a KEY derived from
 * the order before it was sent. That is why one of them may be read on the
 * unit lane's terms and the other is self-evidence.
 */
export interface VariantPricingRates {
  /** `''` is the base row; a request with no declared tier resolves to it. */
  variant_key: string;
  input_per_mtok_usd: number;
  output_per_mtok_usd: number;
  cache_read_per_mtok_usd: number;
  cache_write_per_mtok_usd: number;
  cache_write_5m_per_mtok_usd?: number | null;
  cache_write_1h_per_mtok_usd?: number | null;
  reasoning_per_mtok_usd?: number | null;
  image_input_per_mtok_usd?: number | null;
}

/** One prompt-length pricing tier; rates replace the base row's wholesale. */
export interface PricingTierRates {
  min_prompt_tokens: number;
  input_per_mtok_usd: number;
  output_per_mtok_usd: number;
  cache_read_per_mtok_usd: number;
  cache_write_per_mtok_usd: number;
  cache_write_5m_per_mtok_usd?: number | null;
  cache_write_1h_per_mtok_usd?: number | null;
  reasoning_per_mtok_usd?: number | null;
  image_input_per_mtok_usd?: number | null;
}

export interface MeteredPricingRates {
  metering_profile?: MeteringProfileKind | null;
  input_per_mtok_usd: number;
  output_per_mtok_usd: number;
  cache_read_per_mtok_usd: number;
  cache_write_per_mtok_usd: number;
  cache_write_5m_per_mtok_usd?: number | null;
  cache_write_1h_per_mtok_usd?: number | null;
  reasoning_per_mtok_usd?: number | null;
  /**
   * Per-Mtok rate for `input_tokens_details.image_tokens` (#2004). NULL /
   * absent means "bill those tokens at `input_per_mtok_usd`" — today's
   * cents on every SKU that has not set an image-input rate. 0 is an
   * explicit free rate, not a missing one.
   */
  image_input_per_mtok_usd?: number | null;
  /** Prompt-length tiers; absent or empty means single-rate. */
  tiers?: readonly PricingTierRates[];
  /**
   * TOKEN-lane per-tier rate rows of the resolved generation (#1937). Absent
   * or empty means the generation has no tiers and the base rates above are
   * the whole price list — which is every generation in existence.
   *
   * A row REPLACES the base rates wholesale rather than overriding a subset:
   * the upstream charges a different per-token price on each tier, and a
   * partial override would leave the unlisted dimensions priced at the other
   * tier's rate with nothing saying so.
   */
  variant_rates?: readonly VariantPricingRates[];
  /**
   * ADR-012 §3's exact-match verdict for the TOKEN lane, resolved by the
   * pricing reader from the SKU's stored metering spec (#1937).
   *
   * ABSENT MEANS `default_row_fallback` — today's behaviour — and that is safe
   * for the same structural reason the unit lane's is: the two modes can only
   * differ for an event whose `pricing_variant_key` is non-empty, and a
   * non-empty key can only be produced by a SKU whose stored spec declares a
   * template. A producer that never sets this field is a producer whose SKUs
   * declare no template, and it cannot reach the divergent cell.
   */
  variant_rate_lookup?: UnitRateLookupMode;
  /** Per-unit rate rows for unit-metered profiles (#1650); token profiles ignore it. */
  unit_rates?: readonly UnitPricingRate[];
  /**
   * ADR-012 §3's exact-match money rule, as a VERDICT the pricing reader
   * resolved from the SKU's metering spec (#1878 round two).
   *
   * ABSENT MEANS `default_row_fallback`, i.e. today's behaviour, and that is
   * safe rather than a hole: the two modes can only differ on a charge whose
   * `variant_key` is non-empty, and a non-empty key can only be produced by a
   * spec that declared a template (`resolveVariantKey` answers `''` for every
   * spec that did not). So a producer that never sets this field is a producer
   * whose SKUs have no template, and it cannot reach the divergent cell.
   *
   * It carries the VERDICT and not the spec on purpose. The one thing the
   * price lookup has to know is whether the `(unit, '')` row is admissible;
   * `unitRateLookupModeFor` in `metering-spec.ts` is the single derivation of
   * that bit from (profile, stored spec), shared by every reader, so platform
   * rating and the gateway's coverage gate cannot drift on the RULE. Handing
   * the spec itself down instead would put the whole spec algebra on the
   * pricing path and invite a second, divergent reading of it here.
   *
   * It must NOT be derived from `UnitUsageDetails`. That block is the
   * gateway's persisted claim about one event, and a stored row's rating must
   * not depend on what the gateway believed at settle time — platform
   * re-resolves the profile and its spec from its OWN anchor on every rating
   * precisely so that it does not.
   */
  unit_rate_lookup?: UnitRateLookupMode;
}

/**
 * Whether the `(unit, '')` default price row may serve as a fallback for a
 * charge whose exact `(unit, variant_key)` row is absent.
 *
 * `default_row_fallback` is the incumbent two-level ladder and stays correct
 * for every spec that declares no `variant_key` template — all of which emit
 * `variant_key: ''` and therefore hit the default row by EXACT match anyway.
 *
 * `exact_match_only` is ADR-012 §3's money rule: once a spec declares tiers,
 * an unpriced tier must quarantine as `unit_price_missing` rather than bill at
 * the default rate. Concretely, a 4K-high image whose price row was never
 * listed would otherwise bill $0.04 against a ~$0.39 procurement cost —
 * silently, on a successful delivery, with no error anywhere.
 */
export type UnitRateLookupMode = 'default_row_fallback' | 'exact_match_only';

/**
 * One unit price row, keyed by (unit, variant_key). The price is an INTEGER
 * micro-USD amount per unit — never a float per-Munit rate — so the only
 * floating-point step in a unit charge is the rate-multiplier product.
 * `variant_key: ''` is the default row a missing variant falls back to.
 */
export interface UnitPricingRate {
  unit: UnitUsageUnit;
  variant_key: string;
  rate_usd_micros_per_unit: number;
}

/**
 * How a profile counts the prompt total that {@link selectPricingTier}
 * compares against `min_prompt_tokens`.
 *
 * `input_tokens` — the reported input/prompt count is already the total,
 * because the ingress folds cache reads and writes into it (every
 * OpenAI-shaped profile). `input_plus_cache_tokens` — input is reported net
 * of the cache, so the cache counters must be added back (Anthropic, Gemini,
 * and the legacy bucket).
 *
 * It is a NAMED, EXPORTED derivation rather than an inline condition because
 * the tenant catalog publishes the same fact (`CatalogBillingPromptLengthTiers.basis`)
 * so a tenant can reproduce their own tier. Two spellings of this rule — one
 * that bills and one that is published — is precisely the drift that makes a
 * price list unusable while every test stays green.
 */
export type PromptLengthTierBasis = 'input_tokens' | 'input_plus_cache_tokens';

export function promptLengthTierBasisForProfile(profile: string): PromptLengthTierBasis {
  return profile === 'openai_responses_v2' ||
    profile === 'openai_chat_v1' ||
    profile === 'openai_images_v1'
    ? 'input_tokens'
    : 'input_plus_cache_tokens';
}

/**
 * Select the effective rates for a request's prompt length.
 *
 * The tier input must be the TOTAL prompt as the vendor counts it, which the
 * protocols disagree on: OpenAI's prompt_tokens already contains cache
 * read/write tokens, Anthropic's input_tokens excludes them. Selecting on the
 * raw inputTokens would put the same conversation in different tiers per
 * protocol and undercount Anthropic prompts by the whole cached prefix.
 */
export function selectPricingTier(params: {
  pricing: MeteredPricingRates;
  profile: MeteringProfileKind;
  inputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
}): { effective: MeteredPricingRates; tierMinPromptTokens: number; promptTokensForTier: number } {
  const { pricing, profile, inputTokens, cacheReadTokens, cacheWriteTokens } = params;
  const promptTokensForTier = promptLengthTierBasisForProfile(profile) === 'input_tokens'
    ? inputTokens
    : inputTokens + cacheReadTokens + cacheWriteTokens;
  let selected: PricingTierRates | null = null;
  for (const tier of pricing.tiers ?? []) {
    if (!Number.isSafeInteger(tier.min_prompt_tokens) || tier.min_prompt_tokens <= 0) continue;
    if (tier.min_prompt_tokens > promptTokensForTier) continue;
    if (selected === null || tier.min_prompt_tokens > selected.min_prompt_tokens) selected = tier;
  }
  if (selected === null) {
    return { effective: pricing, tierMinPromptTokens: 0, promptTokensForTier };
  }
  return {
    effective: {
      metering_profile: pricing.metering_profile,
      input_per_mtok_usd: selected.input_per_mtok_usd,
      output_per_mtok_usd: selected.output_per_mtok_usd,
      cache_read_per_mtok_usd: selected.cache_read_per_mtok_usd,
      cache_write_per_mtok_usd: selected.cache_write_per_mtok_usd,
      cache_write_5m_per_mtok_usd: selected.cache_write_5m_per_mtok_usd,
      cache_write_1h_per_mtok_usd: selected.cache_write_1h_per_mtok_usd,
      reasoning_per_mtok_usd: selected.reasoning_per_mtok_usd,
      image_input_per_mtok_usd: selected.image_input_per_mtok_usd,
    },
    tierMinPromptTokens: selected.min_prompt_tokens,
    promptTokensForTier,
  };
}

/**
 * Resolve the TOKEN-lane price tier for one event (#1937), or `null` when the
 * SKU declares tiers and this event's tier has no row.
 *
 * ── WHAT SELECTS IT: THE ORDER, NEVER THE ANSWER ──────────────────────────
 *
 * The key comes from `usageDetails.pricing_variant_key`, which the gateway
 * resolved from canonical REQUEST values at the route gate and froze before
 * dialling. Nothing on this path may read the response — and the temptation is
 * real, because the upstream's own counters correlate almost perfectly with
 * the tier (#1937 measured 40594 tokens on the no-video tier and 80770 on the
 * video one, for the same parameters). Inferring the tier from that number
 * would be third-party evidence setting a priced tier, which ADR-012 §2
 * forbids, and it would hand an upstream a lever on our price list.
 *
 * ── THE TWO MODES, AND WHY ABSENCE IS THE INCUMBENT ONE ───────────────────
 *
 * `default_row_fallback` is today's behaviour and reaches it by two routes at
 * once: a generation with no `variant_rates` returns the base rates untouched,
 * and an event with no key resolves `''`, which IS the base row. Every token
 * event ever stored takes both.
 *
 * `exact_match_only` is the rule a declared template turns on. There is no
 * second rung: an unpriced tier answers `null`, which the caller turns into a
 * `variant_price_missing` zero and the rating engine quarantines.
 *
 * Duplicate rows for one key resolve FIRST-WINS in array order, matching
 * `lookupUnitRate`; the source table's PRIMARY KEY makes that unreachable
 * through the write face, and a deterministic rule beats an arbitrary one for
 * a row that reached the list some other way.
 *
 * A selected variant row DROPS the generation's prompt-length tiers. Those two
 * axes both REPLACE the base rates wholesale, and nothing anywhere declares
 * which wins; applying them in sequence would let a tier row written for the
 * base price list silently overwrite a variant's rates. Refusing to combine
 * them is not a limitation being papered over — it is the only reading that
 * does not invent a precedence rule out of implementation order.
 */
function selectPricingVariant(
  pricing: MeteredPricingRates,
  usageDetails: UsageDetails | null,
): MeteredPricingRates | null {
  const rows = (pricing.variant_rates ?? []).filter(
    (candidate) => candidate !== null && typeof candidate === 'object',
  );
  const mode = pricing.variant_rate_lookup ?? 'default_row_fallback';
  const wanted = usageDetails?.pricing_variant_key ?? '';
  const exact = rows.find((candidate) => candidate.variant_key === wanted);
  if (exact === undefined) {
    return mode === 'exact_match_only' ? null : pricing;
  }
  return {
    metering_profile: pricing.metering_profile,
    input_per_mtok_usd: exact.input_per_mtok_usd,
    output_per_mtok_usd: exact.output_per_mtok_usd,
    cache_read_per_mtok_usd: exact.cache_read_per_mtok_usd,
    cache_write_per_mtok_usd: exact.cache_write_per_mtok_usd,
    cache_write_5m_per_mtok_usd: exact.cache_write_5m_per_mtok_usd,
    cache_write_1h_per_mtok_usd: exact.cache_write_1h_per_mtok_usd,
    reasoning_per_mtok_usd: exact.reasoning_per_mtok_usd,
    image_input_per_mtok_usd: exact.image_input_per_mtok_usd,
  };
}

export function computeCostUsdCents(params: {
  pricing: MeteredPricingRates;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  usageDetails?: UsageDetails | null;
  /** Channel/user-facing multiplier. Defaults to base model price. */
  rateMultiplier?: number;
}): number {
  return computeCostBreakdownUsdCents(params).costUsdCents;
}

export function computeCostUsdMicros(params: {
  pricing: MeteredPricingRates;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  usageDetails?: UsageDetails | null;
  /** Channel/user-facing multiplier. Defaults to base model price. */
  rateMultiplier?: number;
}): number {
  return computeCostBreakdownUsdMicros(params).costUsdMicros;
}

export function computeCostBreakdownUsdCents(params: {
  pricing: MeteredPricingRates;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  usageDetails?: UsageDetails | null;
  /** Channel/user-facing or upstream-cost multiplier. Defaults to base model price. */
  rateMultiplier?: number;
}): { costUsdCents: number; breakdown: UsageCostBreakdown } {
  const computed = computeCostBreakdownUsdMicros(params);
  return { costUsdCents: computed.costUsdCents, breakdown: computed.breakdown };
}

export function computeCostBreakdownUsdMicros(params: {
  pricing: MeteredPricingRates;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  usageDetails?: UsageDetails | null;
  /** Channel/user-facing or upstream-cost multiplier. Defaults to base model price. */
  rateMultiplier?: number;
  /**
   * Whether this request came through an ingress that can only ask for an
   * image. See the `openai_images_v1` note above — in particular why this must
   * NOT be derived from the request body.
   */
  imageRequested?: boolean;
}): { costUsdMicros: number; costUsdCents: number; breakdown: UsageCostBreakdown } {
  const {
    pricing,
    inputTokens,
    outputTokens,
    cacheReadTokens,
    cacheWriteTokens,
    usageDetails = null,
    rateMultiplier = 1,
    imageRequested = false,
  } = params;
  const profile = resolveProfile(pricing, usageDetails);
  // Unit-metered profiles bill counted units, never tokens (#1650). The branch
  // keys off the RESOLVED profile, so a token profile ignores a stray
  // `unit_usage` block entirely and a unit profile ignores every token
  // counter — neither side can double-bill the other's dimension.
  //
  // `unit_surcharges` is the exception that is NOT a lane swap (#2150): a
  // token event that carries a non-empty list adds those terms to the token
  // charge. The gate is the list, not `isUnitMeteredProfileKind`. `unit_usage`
  // stays ignored on the token lane.
  if (isUnitMeteredProfileKind(profile)) {
    return computeUnitCostBreakdown({
      pricing,
      profile,
      unitUsage: usageDetails?.unit_usage,
      unitSurcharges: usageDetails?.unit_surcharges,
      rateMultiplier,
    });
  }
  // A soft refusal: an image was asked for and none came back. The upstream
  // spent quota on it either way, and that cost is absorbed rather than passed
  // on — a caller cannot be charged for a delivery that did not happen, and
  // cannot retry cheaply if it is.
  //
  // "No image came back" is read off the OBSERVED profile, not off a zero
  // output count. The two are not the same thing and the difference is the
  // whole defect this replaced: a soft refusal is the model answering in prose,
  // so its output count is ~80 text tokens, not 0. Testing for zero output
  // therefore never fired, and the tenant paid the image rate for a sentence
  // saying no. The extractor labels a response `openai_images_v1` exactly when
  // it found image tokens in it, on either wire shape, so that label is the
  // delivery evidence.
  //
  // Narrow on the other side too: it fires only when the request asked, so a
  // plain text request to an image SKU keeps paying for the text it received.
  const deliveredImage = usageDetails?.profile === 'openai_images_v1' && outputTokens > 0;
  if (profile === 'openai_images_v1' && imageRequested && !deliveredImage) {
    const breakdown = buildZeroBreakdown(profile, rateMultiplier, 'image_not_produced');
    return { costUsdMicros: 0, costUsdCents: 0, breakdown };
  }
  if (usageDetails?.invalid_dimensions !== undefined && usageDetails.invalid_dimensions.length > 0) {
    const breakdown = buildInvalidUsageBreakdown(profile, rateMultiplier);
    return { costUsdMicros: 0, costUsdCents: 0, breakdown };
  }
  if (!Number.isFinite(rateMultiplier) || rateMultiplier <= 0) {
    const breakdown = buildCostOverflowBreakdown(profile, rateMultiplier);
    return { costUsdMicros: 0, costUsdCents: 0, breakdown };
  }
  // ── The TOKEN-lane price TIER (#1937), before anything else is priced ────
  //
  // It runs here — inside the shared kernel, above both the legacy fallback
  // and the prompt-length tier — for the reason that makes the procurement
  // side free: `computeUpstreamCostBreakdown` in platform rating calls THIS
  // function with the SAME `pricing` object and only a different
  // `rateMultiplier`, so a tier selected here is selected for the tenant
  // charge and the procurement cost by one line of code. There is no second
  // place to keep in step, and no `upstream_channel_model_costs` column to
  // drift: the procurement multiplier stays the scalar discount it always was.
  const variant = selectPricingVariant(pricing, usageDetails);
  if (variant === null) {
    // ADR-012 §3's exact-match rule on the token lane: the SKU declares tiers
    // and this one has no price row. Never the base row — that row is sized
    // for a differently-shaped request, and #1937 measured the two rates 64%
    // apart with the cheaper-per-token tier costing MORE per call.
    const breakdown = buildZeroBreakdown(profile, rateMultiplier, 'variant_price_missing');
    return { costUsdMicros: 0, costUsdCents: 0, breakdown };
  }
  // The tier resolves once, BEFORE the legacy-fallback branch below, so a
  // response without parseable usage still bills at the tier its prompt
  // qualified for. Both settlement calls (tenant + upstream multiplier) pass
  // the same pricing object, so both land on the same tier by construction.
  const tierSelection = selectPricingTier({
    pricing: variant,
    profile,
    inputTokens,
    cacheReadTokens,
    cacheWriteTokens,
  });
  const effectivePricing = tierSelection.effective;
  const billable = usageDetails?.billable;
  const pricesAnthropicCacheTtl = profile === 'anthropic_messages_v2';
  const pricesOpenAiCache = profile === 'openai_responses_v2' ||
    profile === 'openai_chat_v1' ||
    profile === 'openai_images_v1';
  const pricesReasoning = profile === 'gemini_generate_content_v1';
  const ordinaryInputTokens = pricesOpenAiCache
    ? Math.max(0, inputTokens - cacheReadTokens - cacheWriteTokens)
    : inputTokens;
  const cacheWrite5mTokens = pricesAnthropicCacheTtl
    ? billable?.cache_creation_ephemeral_5m_input_tokens ?? 0
    : 0;
  const cacheWrite1hTokens = pricesAnthropicCacheTtl
    ? billable?.cache_creation_ephemeral_1h_input_tokens ?? 0
    : 0;
  const missing: string[] = [];
  if (pricesOpenAiCache && cacheWriteTokens > 0 && !hasPositivePrice(effectivePricing.cache_write_per_mtok_usd)) {
    missing.push('cache_creation_input_tokens');
  }
  if (cacheWrite5mTokens > 0 && !hasPositivePrice(effectivePricing.cache_write_5m_per_mtok_usd)) {
    missing.push('cache_creation.ephemeral_5m_input_tokens');
  }
  if (cacheWrite1hTokens > 0 && !hasPositivePrice(effectivePricing.cache_write_1h_per_mtok_usd)) {
    missing.push('cache_creation.ephemeral_1h_input_tokens');
  }
  const reasoningTokens = pricesReasoning ? billable?.reasoning_tokens ?? 0 : 0;
  if (reasoningTokens > 0 && !hasPositivePrice(effectivePricing.reasoning_per_mtok_usd)) {
    missing.push('reasoning_tokens');
  }
  // Image-input tokens are a subset of the uncached remainder, never an extra
  // counter, and never a reason to fall back to the legacy 4-bucket path. A
  // NULL `image_input_per_mtok_usd` bills them at the text input rate so a
  // stock SKU's cents do not move; a missing count (every pre-#2004 event)
  // is 0, which is the same all-at-text-rate charge.
  const pricesImageInput = profile === 'openai_images_v1';
  const reportedImageInputTokens = pricesImageInput
    ? Math.max(0, billable?.image_input_tokens ?? 0)
    : 0;
  // Cap against max(0, ordinary) so a negative input total — the overflow
  // sentinel `rawDimension` already handles — cannot be turned into a
  // negative image count by `Math.min(0, -1)`.
  const imageInputTokens = Math.min(reportedImageInputTokens, Math.max(0, ordinaryInputTokens));
  const textInputTokens = ordinaryInputTokens - imageInputTokens;
  const imageInputRateUsd = configuredImageInputRate(effectivePricing);

  if (usageDetails === null || profile === 'legacy_4_bucket' || missing.length > 0) {
    const legacy = buildLegacyBreakdown({
      pricing: effectivePricing,
      profile,
      inputTokens,
      outputTokens,
      cacheReadTokens,
      cacheWriteTokens,
      rateMultiplier,
      missing,
      usageDetails,
      // Surcharge unit_rates live on the ORIGINAL pricing object. Variant
      // and prompt-length selection replace the token rates wholesale and
      // drop `unit_rates`; looking them up on `effectivePricing` would
      // make every surcharge `unit_price_missing` the moment a tier hits.
      surchargePricing: pricing,
      tierMinPromptTokens: tierSelection.tierMinPromptTokens,
      tierPromptTokens: tierSelection.promptTokensForTier,
    });
    return {
      costUsdMicros: legacy.cost_usd_micros,
      costUsdCents: legacy.cost_usd_cents,
      breakdown: legacy,
    };
  }

  const rawDimensions: RawCostDimension[] = [
    rawDimension('input_tokens', textInputTokens, effectivePricing.input_per_mtok_usd, rateMultiplier),
    rawDimension('output_tokens', outputTokens, effectivePricing.output_per_mtok_usd, rateMultiplier),
    rawDimension('cache_read_input_tokens', cacheReadTokens, effectivePricing.cache_read_per_mtok_usd, rateMultiplier),
  ];
  if (pricesImageInput && imageInputTokens > 0) {
    rawDimensions.push(
      rawDimension('image_input_tokens', imageInputTokens, imageInputRateUsd, rateMultiplier),
    );
  }
  if (pricesOpenAiCache) {
    rawDimensions.push(
      rawDimension('cache_creation_input_tokens', cacheWriteTokens, effectivePricing.cache_write_per_mtok_usd, rateMultiplier),
    );
  }
  if (pricesAnthropicCacheTtl) {
    const cacheWriteOtherTokens = Math.max(0, cacheWriteTokens - cacheWrite5mTokens - cacheWrite1hTokens);
    rawDimensions.push(
      rawDimension('cache_creation.other_input_tokens', cacheWriteOtherTokens, effectivePricing.cache_write_per_mtok_usd, rateMultiplier),
      rawDimension(
        'cache_creation.ephemeral_5m_input_tokens',
        cacheWrite5mTokens,
        effectivePricing.cache_write_5m_per_mtok_usd ?? 0,
        rateMultiplier,
      ),
      rawDimension(
        'cache_creation.ephemeral_1h_input_tokens',
        cacheWrite1hTokens,
        effectivePricing.cache_write_1h_per_mtok_usd ?? 0,
        rateMultiplier,
      ),
    );
  }
  if (pricesReasoning) {
    rawDimensions.push(
      rawDimension('reasoning_tokens', reasoningTokens, effectivePricing.reasoning_per_mtok_usd ?? 0, rateMultiplier),
    );
  }
  return finishTokenCharge({
    pricing,
    profile,
    rateMultiplier,
    rawDimensions,
    tokenUnpriced: [],
    fallbackToLegacy: false,
    usageDetails,
    tierMinPromptTokens: tierSelection.tierMinPromptTokens,
    tierPromptTokens: tierSelection.promptTokensForTier,
  });
}

/**
 * The kernel's lane selection. Three terms, this order, and this order
 * only: the price row, then the persisted document, then
 * `legacy_4_bucket` (a token lane).
 *
 * Rating's token-lane surcharge-strip (#2150) must call this rather than
 * reading `usageDetails.profile` alone. A spec that moved between settle
 * and rating is a real shape; the two disagreeing is how silent
 * undercharge (strip on a unit price row) and silent reprice (no strip
 * on a token price row, body falls onto the legacy four-bucket path)
 * both happen.
 *
 * The optional `pricing` form is equivalent to a row with a null
 * `metering_profile`. It exists so a caller that does not have a price
 * row still shares this function instead of copying the three-term
 * chain. Production rating always passes the resolved row.
 *
 * Do not reverse the terms. Widening the argument types to Picks / null
 * does not change what the kernel computes: it still passes a full
 * `MeteredPricingRates` and a full `UsageDetails | null`.
 */
export function resolveProfile(
  pricing: Pick<MeteredPricingRates, 'metering_profile'> | null | undefined,
  usageDetails: Pick<UsageDetails, 'profile'> | null,
): MeteringProfileKind {
  return pricing?.metering_profile ?? usageDetails?.profile ?? 'legacy_4_bucket';
}

/**
 * Price one unit-metered charge (#1650). Gate order, and why it is that order:
 *
 * 1. `unit_usage` absent, `null`, or not an object → `invalid_usage`: the
 *    usage this profile needs is not there, the same defect class as
 *    `invalid_dimensions` on tokens. `null` is JSON's most common degenerate
 *    form and `typeof null === 'object'`, so it is checked explicitly —
 *    dereferencing it would turn one poisoned event into a permanently
 *    replaying sweep. An array passes the typeof test, but its `.unit` is
 *    undefined and the closed-set gate below answers.
 * 2. runtime shape gate → `invalid_usage`: `unit_usage` rides in parsed JSON,
 *    so the static types prove nothing at this boundary. An `unit` outside
 *    the closed set, a non-string `variant_key`, or a non-number `quantity`
 *    is the same defect class as the block being absent, and it must answer
 *    BEFORE the delivery gate — a mistyped block must not be silently filed
 *    as an undelivered freebie.
 * 3. malformed numeric `quantity` (negative / fractional / non-finite /
 *    beyond safe-integer) → `cost_overflow`: a numeric counter that cannot
 *    be trusted, the same bucket negative token counts land in on the token
 *    path.
 * 4. `delivered !== true` → `unit_not_delivered`: delivery must be PROVEN,
 *    never presumed — an absent or mistyped flag absorbs the charge exactly
 *    like an explicit `false`. Mirrors `image_not_produced`: a caller cannot
 *    be charged for a delivery that did not happen; the upstream spend is
 *    absorbed, with the reason recorded.
 * 5. no usable price row → `unit_price_missing`: a DISTINCT marker, never a
 *    silent zero, so rating can quarantine instead of failing open. It fires
 *    before the multiplier gate on purpose — `cost_overflow` rates as an
 *    ordinary anomaly, and letting a bad multiplier mask a missing price
 *    would hide exactly the misconfiguration the marker exists to surface.
 * 6. multiplier zero/negative/NaN → `cost_overflow`, same semantics as the
 *    token path's multiplier gate.
 * 7. otherwise `quantity × rate × multiplier`; the count and the rate are
 *    both integers and their product is required to stay a safe integer, so
 *    the multiplier product is the only floating-point step, and either
 *    overflow lands in `cost_overflow`.
 *
 * The token path's `invalid_dimensions` gate deliberately does NOT apply
 * here: it reports untrustworthy TOKEN counters, and a unit charge reads no
 * token counter, so corrupt token telemetry must not void a unit charge
 * whose own `unit_usage` block is intact.
 *
 * `selectPricingTier` never participates: prompt-length tiers are a token
 * concept and a unit charge has no prompt.
 */
function computeUnitCostBreakdown(params: {
  pricing: MeteredPricingRates;
  profile: UnitMeteredProfileKind;
  unitUsage: UnitUsageDetails | undefined;
  unitSurcharges: readonly UnitUsageDetails[] | undefined;
  rateMultiplier: number;
}): { costUsdMicros: number; costUsdCents: number; breakdown: UsageCostBreakdown } {
  const { pricing, profile, unitUsage, unitSurcharges, rateMultiplier } = params;
  const zero = (
    reason: 'cost_overflow' | 'invalid_usage' | 'unit_not_delivered' | 'unit_price_missing',
  ): { costUsdMicros: number; costUsdCents: number; breakdown: UsageCostBreakdown } => ({
    costUsdMicros: 0,
    costUsdCents: 0,
    breakdown: buildZeroBreakdown(profile, rateMultiplier, reason),
  });
  if (unitUsage === undefined || unitUsage === null || typeof unitUsage !== 'object') {
    return zero('invalid_usage');
  }
  if (
    !(KNOWN_UNIT_USAGE_UNITS as readonly string[]).includes(unitUsage.unit) ||
    typeof unitUsage.variant_key !== 'string' ||
    typeof unitUsage.quantity !== 'number'
  ) {
    return zero('invalid_usage');
  }
  if (!Number.isSafeInteger(unitUsage.quantity) || unitUsage.quantity < 0) {
    return zero('cost_overflow');
  }
  if (unitUsage.delivered !== true) {
    return zero('unit_not_delivered');
  }
  // ADR-012 §3's exact-match rule lands HERE, not inside `lookupUnitRate`:
  // this is the seat that holds the profile and the resolved pricing, and the
  // lookup below deliberately sees only the price table. A reader that never
  // resolved a spec keeps the incumbent ladder, so nothing changes for the
  // profiles that bill today.
  const lookupMode = pricing.unit_rate_lookup ?? 'default_row_fallback';
  const rate = lookupUnitRate(
    pricing,
    unitUsage.unit,
    unitUsage.variant_key,
    lookupMode,
  );
  if (rate === null) {
    return zero('unit_price_missing');
  }
  if (!Number.isFinite(rateMultiplier) || rateMultiplier <= 0) {
    return zero('cost_overflow');
  }
  const integerMicros = unitUsage.quantity * rate;
  if (!Number.isSafeInteger(integerMicros)) {
    return zero('cost_overflow');
  }
  // Absent and `[]` are the same read: no surcharge. A non-array is a
  // malformed document. Duplicate `(unit, variant_key)` pairs are also
  // malformed — never summed.
  if (unitSurcharges !== undefined && !Array.isArray(unitSurcharges)) {
    return zero('invalid_usage');
  }
  const surcharges = unitSurcharges ?? [];

  // Rounding rule (#2149), decided once: compute the exact micros of each
  // term, SUM, round the TOTAL once, then allocate the total across the
  // dimension rows by largest remainder so the rows sum exactly to the
  // total. Rounding each term separately and adding is a systematic drift.
  // A single term still degenerates to identity — asserted, not run —
  // which is why a zero-image event stays byte-identical to the
  // pre-surcharge build.
  if (surcharges.length === 0) {
    const rawMicros = integerMicros * rateMultiplier;
    const costUsdMicros = safeRoundMicros(rawMicros);
    if (costUsdMicros === null) {
      return zero('cost_overflow');
    }
    const costUsdCents = usdMicrosToCentsFloor(costUsdMicros);
    const dimension: UnitCostBreakdownDimension = {
      dimension: 'unit',
      unit: unitUsage.unit,
      quantity: unitUsage.quantity,
      variant_key: unitUsage.variant_key,
      rate_usd_micros_per_unit: rate,
      usd_micros: costUsdMicros,
      usd_cents: costUsdCents,
    };
    const breakdown: UsageCostBreakdown = {
      schema_version: 1,
      profile,
      rate_multiplier: rateMultiplier,
      cost_usd_micros: costUsdMicros,
      cost_usd_cents: costUsdCents,
      fallback_to_legacy_4_bucket: false,
      unpriced_dimensions: [],
      dimensions: [dimension],
      pricing_tier_min_prompt_tokens: 0,
      pricing_tier_prompt_tokens: 0,
    };
    return { costUsdMicros, costUsdCents, breakdown };
  }

  const mainRaw = integerMicros * rateMultiplier;
  const parts: {
    readonly unit: UnitUsageUnit;
    readonly quantity: number;
    readonly variant_key: string;
    readonly rate_usd_micros_per_unit: number;
    readonly raw_micros: number;
  }[] = [
    {
      unit: unitUsage.unit,
      quantity: unitUsage.quantity,
      variant_key: unitUsage.variant_key,
      rate_usd_micros_per_unit: rate,
      raw_micros: mainRaw,
    },
  ];
  const seenPairs = new Set<string>();
  let totalRaw = mainRaw;
  for (const entry of surcharges) {
    const priced = priceUnitTerm(pricing, entry, rateMultiplier, lookupMode);
    if (!priced.ok) {
      return zero(priced.reason);
    }
    const pairKey = `${entry.unit}\u001f${entry.variant_key}`;
    if (seenPairs.has(pairKey)) {
      return zero('invalid_usage');
    }
    seenPairs.add(pairKey);
    totalRaw += priced.rawMicros;
    parts.push({
      unit: entry.unit,
      quantity: entry.quantity,
      variant_key: entry.variant_key,
      rate_usd_micros_per_unit: priced.rate,
      raw_micros: priced.rawMicros,
    });
  }
  const costUsdMicros = safeRoundMicros(totalRaw);
  if (costUsdMicros === null) {
    return zero('cost_overflow');
  }
  const dimensions = allocateUnitDimensions(parts, costUsdMicros);
  const costUsdCents = usdMicrosToCentsFloor(costUsdMicros);
  const breakdown: UsageCostBreakdown = {
    schema_version: 1,
    profile,
    rate_multiplier: rateMultiplier,
    cost_usd_micros: costUsdMicros,
    cost_usd_cents: costUsdCents,
    fallback_to_legacy_4_bucket: false,
    unpriced_dimensions: [],
    dimensions,
    pricing_tier_min_prompt_tokens: 0,
    pricing_tier_prompt_tokens: 0,
  };
  return { costUsdMicros, costUsdCents, breakdown };
}

/**
 * Shape-gate, rate-lookup and multiplier product for one unit term.
 *
 * Lane-neutral and pure: takes `(pricing, usage, rateMultiplier,
 * lookupMode)` and knows nothing about which lane called it. The unit
 * branch calls it today; router#2150 will call it from the TOKEN lane.
 * Do not couple this helper to the unit breakdown, and do not join its
 * output onto `finalizeDimensions` here — the mix (one round over
 * tokenRaw + surchargeRaw, one remainder allocation, then each row
 * rendered in its own shape) is `finishTokenCharge` below.
 *
 * A malformed or unpriced term returns the same zero marker the primary
 * unit term would. The UNIT lane treats that as a whole-event
 * quarantine; the TOKEN lane skips the term and keeps the token body.
 */
function priceUnitTerm(
  pricing: MeteredPricingRates,
  usage: UnitUsageDetails,
  rateMultiplier: number,
  lookupMode: UnitRateLookupMode,
):
  | { ok: true; rate: number; rawMicros: number }
  | {
      ok: false;
      reason: 'cost_overflow' | 'invalid_usage' | 'unit_not_delivered' | 'unit_price_missing';
    } {
  if (usage === null || typeof usage !== 'object') {
    return { ok: false, reason: 'invalid_usage' };
  }
  if (
    !(KNOWN_UNIT_USAGE_UNITS as readonly string[]).includes(usage.unit) ||
    typeof usage.variant_key !== 'string' ||
    typeof usage.quantity !== 'number'
  ) {
    return { ok: false, reason: 'invalid_usage' };
  }
  if (!Number.isSafeInteger(usage.quantity) || usage.quantity < 0) {
    return { ok: false, reason: 'cost_overflow' };
  }
  // ── UNREACHABLE TODAY ON BOTH LANES, FOR TWO DIFFERENT REASONS ──────────
  //
  // Read this before deleting the check as dead code or citing it as a live
  // protection. Both readings are wrong, and the reason differs per lane — so
  // a single sentence about "the" delivery gate is what to avoid writing.
  //
  // UNIT LANE (Grok video reference images, the one producer today): the
  // PRIMARY term's `delivered` is checked by the caller before any entry is
  // priced, so nothing reaches here on an undelivered job. The producer also
  // authors every entry's `delivered` from the SAME job-delivery fact as the
  // primary block, so a delivered primary beside an undelivered entry is not
  // a shape it can emit. The entry's gate is INHERITED, not independent.
  //
  // TOKEN LANE (router#2150): there is no primary `unit_usage` to inherit
  // from at all — that lane never enters `computeUnitCostBreakdown` — so
  // this check is the ONLY delivery gate an entry meets. It is unreachable
  // there for a different reason: a surcharge derived from a vendor's own
  // tool counter is structurally delivered (the vendor reporting the call
  // IS the call happening; a call that did not happen leaves the counter
  // at zero and produces no entry at all). `finishTokenCharge` still
  // honours a `false` by skipping THAT entry, not zeroing the token body.
  //
  // So the check is not decoration and it is not redundant. On the unit lane
  // it is depth behind an inherited gate; on the token lane it is the only
  // gate there is, load-bearing the moment any producer there can write
  // `false`. Deleting it because "the primary already checks" would be
  // deleting the token lane's only delivery gate on the strength of a
  // property only the unit lane has.
  if (usage.delivered !== true) {
    return { ok: false, reason: 'unit_not_delivered' };
  }
  // The rate is per MODEL. `MeteredPricingRates.unit_rates` is the
  // `(vendor, model)` generation `readEventTimePricing` already resolved
  // (`model_unit_pricing` WHERE vendor = req.vendor AND model = req.model);
  // the gateway coverage gate keys the same way
  // (`isPubUnitVariantPriced(snapshot, vendor, model, …)`). There is no
  // profile-wide or vendor-wide surcharge constant. Measured 2026-08-27:
  // grok-imagine-video-1.5 adds 100,000,000 ticks ($0.01) per image;
  // grok-imagine-video adds 20,000,000 ticks ($0.002) — five times apart.
  // Collapsing this lookup to one rate is an 11-cent-per-job overcharge
  // at 14 images.
  const rate = lookupUnitRate(pricing, usage.unit, usage.variant_key, lookupMode);
  if (rate === null) {
    return { ok: false, reason: 'unit_price_missing' };
  }
  const integerMicros = usage.quantity * rate;
  if (!Number.isSafeInteger(integerMicros)) {
    return { ok: false, reason: 'cost_overflow' };
  }
  return { ok: true, rate, rawMicros: integerMicros * rateMultiplier };
}

interface TokenSurchargePart {
  readonly unit: UnitUsageUnit;
  readonly quantity: number;
  readonly variant_key: string;
  readonly rate_usd_micros_per_unit: number;
  readonly raw_micros: number;
}

/**
 * Round-and-allocate the token charge, optionally adding priced
 * `unit_surcharges` (#2150).
 *
 * THE GATE IS THE EVENT'S DATA, NEVER THE PROFILE'S LANE. A token SKU
 * without this block returns through the pre-surcharge arm below, which
 * is the `safeRoundMicros` + `finalizeDimensions` sequence the golden
 * suite pins. Entering the mix because `!isUnitMeteredProfileKind` would
 * put every token SKU on the platform through unproven code.
 *
 * One round over `tokenRaw + surchargeRaw`, then one remainder
 * allocation over the concatenated raw parts. Each row is then rendered
 * in its own shape: token rows keep `tokens` / `per_mtok_usd`, unit rows
 * keep `unit` / `quantity` / `variant_key` / `rate_usd_micros_per_unit`.
 * That is `finalizeDimensions`'s allocation, decoupled from the token
 * row shape — not a second allocator.
 *
 * An unpriced or malformed surcharge term is SKIPPED and recorded on
 * `unpriced_dimensions`. The token body is kept. This is the opposite
 * of the unit lane (`computeUnitCostBreakdown` zeroes the whole event
 * on the same failure) because there the body IS the unit charge, and
 * here the body has already been delivered.
 *
 * `unit_rates` are read from the caller's `pricing`, which must be the
 * original generation object: variant / prompt-length selection replace
 * the token rates wholesale and drop `unit_rates`.
 */
function finishTokenCharge(params: {
  pricing: MeteredPricingRates;
  profile: MeteringProfileKind;
  rateMultiplier: number;
  rawDimensions: readonly RawCostDimension[];
  tokenUnpriced: readonly string[];
  fallbackToLegacy: boolean;
  usageDetails: UsageDetails | null;
  tierMinPromptTokens: number;
  tierPromptTokens: number;
}): { costUsdMicros: number; costUsdCents: number; breakdown: UsageCostBreakdown } {
  const {
    pricing,
    profile,
    rateMultiplier,
    rawDimensions,
    tokenUnpriced,
    fallbackToLegacy,
    usageDetails,
    tierMinPromptTokens,
    tierPromptTokens,
  } = params;
  const tokenRaw = rawDimensions.reduce((sum, part) => sum + part.raw_micros, 0);
  const surchargeList = usageDetails?.unit_surcharges;
  // Absent, a non-array, and `[]` all take the original arm. `[]` is the
  // documented "no surcharge" read; treating it as a mix input would
  // still round the same total but would allocate over a concatenated
  // list whose second half is empty — a different code path for a
  // document that must stay byte-identical to one that never had the
  // field.
  if (!Array.isArray(surchargeList) || surchargeList.length === 0) {
    const costUsdMicros = safeRoundMicros(tokenRaw);
    if (costUsdMicros === null) {
      const breakdown = buildCostOverflowBreakdown(profile, rateMultiplier);
      return { costUsdMicros: 0, costUsdCents: 0, breakdown };
    }
    const dimensions = finalizeDimensions(rawDimensions, costUsdMicros);
    const costUsdCents = usdMicrosToCentsFloor(costUsdMicros);
    const breakdown: UsageCostBreakdown = {
      schema_version: 1,
      profile,
      rate_multiplier: rateMultiplier,
      cost_usd_micros: costUsdMicros,
      cost_usd_cents: costUsdCents,
      fallback_to_legacy_4_bucket: fallbackToLegacy,
      unpriced_dimensions: tokenUnpriced,
      dimensions,
      pricing_tier_min_prompt_tokens: tierMinPromptTokens,
      pricing_tier_prompt_tokens: tierPromptTokens,
    };
    return { costUsdMicros, costUsdCents, breakdown };
  }

  // Token SKUs do not currently carry `unit_rate_lookup` (that fragment
  // is empty for non-unit profiles). Defaulting to exact-match is the
  // ADR-012 #2150 rule: an unpriced tool tier is never billed at a
  // neighbouring `(unit, '')` row. The unit lane still defaults to
  // `default_row_fallback` for incumbent profiles that emit `''`.
  const lookupMode = pricing.unit_rate_lookup ?? 'exact_match_only';
  const priced = priceTokenLaneSurcharges(pricing, surchargeList, rateMultiplier, lookupMode);
  const costUsdMicros = safeRoundMicros(tokenRaw + priced.rawMicros);
  if (costUsdMicros === null) {
    const breakdown = buildCostOverflowBreakdown(profile, rateMultiplier);
    return { costUsdMicros: 0, costUsdCents: 0, breakdown };
  }
  const dimensions = priced.parts.length === 0
    ? finalizeDimensions(rawDimensions, costUsdMicros)
    : finalizeMixedDimensions(rawDimensions, priced.parts, costUsdMicros);
  const costUsdCents = usdMicrosToCentsFloor(costUsdMicros);
  const unpriced = priced.unpriced.length === 0
    ? tokenUnpriced
    : [...tokenUnpriced, ...priced.unpriced];
  const breakdown: UsageCostBreakdown = {
    schema_version: 1,
    profile,
    rate_multiplier: rateMultiplier,
    cost_usd_micros: costUsdMicros,
    cost_usd_cents: costUsdCents,
    fallback_to_legacy_4_bucket: fallbackToLegacy,
    unpriced_dimensions: unpriced,
    dimensions,
    pricing_tier_min_prompt_tokens: tierMinPromptTokens,
    pricing_tier_prompt_tokens: tierPromptTokens,
  };
  return { costUsdMicros, costUsdCents, breakdown };
}

/**
 * Price each token-lane surcharge term independently. Failures skip the
 * term; they do not zero the token body. Duplicate `(unit, variant_key)`
 * pairs are not summed — the first occurrence bills, later copies are
 * skipped as `invalid_usage`. That is weaker than the unit lane (which
 * quarantines the whole event) on purpose: the token body was delivered.
 *
 * Through the rating path, a duplicate pair is rejected at document
 * parse (unit lane: whole-document quarantine; token lane: the list is
 * stripped and marked). This branch is reachable only when the kernel
 * is called directly with a well-typed list that still contains a
 * duplicate — do not read it as the live rating behaviour.
 */
function priceTokenLaneSurcharges(
  pricing: MeteredPricingRates,
  entries: readonly UnitUsageDetails[],
  rateMultiplier: number,
  lookupMode: UnitRateLookupMode,
): {
  parts: TokenSurchargePart[];
  rawMicros: number;
  unpriced: string[];
} {
  const parts: TokenSurchargePart[] = [];
  const unpriced: string[] = [];
  const seenPairs = new Set<string>();
  let rawMicros = 0;
  for (const entry of entries) {
    const priced = priceUnitTerm(pricing, entry, rateMultiplier, lookupMode);
    const unit = entry !== null && typeof entry === 'object' && typeof entry.unit === 'string'
      ? entry.unit
      : '';
    const variantKey = entry !== null && typeof entry === 'object' && typeof entry.variant_key === 'string'
      ? entry.variant_key
      : '';
    if (!priced.ok) {
      recordTokenSurchargeUnpriced(unpriced, priced.reason, unit, variantKey);
      continue;
    }
    const pairKey = `${unit}\u001f${variantKey}`;
    if (seenPairs.has(pairKey)) {
      recordTokenSurchargeUnpriced(unpriced, 'invalid_usage', unit, variantKey);
      continue;
    }
    seenPairs.add(pairKey);
    rawMicros += priced.rawMicros;
    parts.push({
      unit: entry.unit,
      quantity: entry.quantity,
      variant_key: entry.variant_key,
      rate_usd_micros_per_unit: priced.rate,
      raw_micros: priced.rawMicros,
    });
  }
  return { parts, rawMicros, unpriced };
}

/**
 * Every skipped token-lane surcharge must leave a marker the ops-console
 * parser can see. That parser (`isOneOf(marker, UNPRICED_REASONS)`) is
 * exact membership: a suffixed forensic string alone is silently dropped,
 * which is how `variant_price_missing` once made ADR-012 §3's "loudly
 * free" into silently free. The member reason is the visible loss; the
 * `${reason}:${unit}:${variantKey}` copy is extra evidence, never a
 * substitute.
 *
 * `unpriced_dimensions` has two consumers. ops-console reads these markers
 * as visibility. The batch allocator (`internal-batch-allocate.ts`) treats
 * `unpriced_dimensions.length > 0` as "the whole procurement price is
 * unknown" and drops the event — including the token-body procurement
 * attribution — into unknown, so it never enters a funding batch. Tenant
 * ledger stays correct (tokens are still charged); only procurement
 * attribution is lost, and only on a surcharge-pricing failure that is
 * already alarming. That cross-seat drop is a known, accepted degradation.
 * Converge it in a separate issue; do not quietly change the allocator here.
 */
function recordTokenSurchargeUnpriced(
  unpriced: string[],
  reason: string,
  unit: string,
  variantKey: string,
): void {
  if (!unpriced.includes(reason)) {
    unpriced.push(reason);
  }
  unpriced.push(`${reason}:${unit}:${variantKey}`);
}

/**
 * Largest-remainder allocation, shape-agnostic. `finalizeDimensions` and
 * `allocateUnitDimensions` both render from this so a mixed token+unit
 * list can reuse the same remainder rule without a third copy.
 */
function allocateRemainders(rawMicros: readonly number[], totalMicros: number): number[] {
  const floors = rawMicros.map((raw, index) => ({
    index,
    micros: Math.floor(raw),
    remainder: raw - Math.floor(raw),
  }));
  let remaining = Math.max(0, totalMicros - floors.reduce((sum, part) => sum + part.micros, 0));
  const byRemainder = [...floors].sort((a, b) => b.remainder - a.remainder || a.index - b.index);
  for (const part of byRemainder) {
    if (remaining <= 0) break;
    part.micros += 1;
    remaining -= 1;
  }
  return floors.map((part) => part.micros);
}

/**
 * Unit-shaped twin of {@link finalizeDimensions}: allocate a rounded
 * total across unit rows by largest remainder so the rows sum EXACTLY
 * to the total. Cents on the event are the floor of that total, not
 * the sum of per-row floors.
 */
function allocateUnitDimensions(
  parts: readonly {
    readonly unit: UnitUsageUnit;
    readonly quantity: number;
    readonly variant_key: string;
    readonly rate_usd_micros_per_unit: number;
    readonly raw_micros: number;
  }[],
  totalMicros: number,
): UnitCostBreakdownDimension[] {
  const allocated = allocateRemainders(parts.map((part) => part.raw_micros), totalMicros);
  return parts.map((part, index) => {
    const usdMicros = allocated[index] ?? 0;
    return {
      dimension: 'unit' as const,
      unit: part.unit,
      quantity: part.quantity,
      variant_key: part.variant_key,
      rate_usd_micros_per_unit: part.rate_usd_micros_per_unit,
      usd_micros: usdMicros,
      usd_cents: usdMicrosToCentsFloor(usdMicros),
    };
  });
}

/**
 * Token rows then unit rows, allocated together so the mixed list sums
 * to the single rounded total. Token filter (`tokens > 0 || raw > 0`)
 * is the same cut `finalizeDimensions` uses, applied before the unit
 * parts are appended, so a no-unit call would match it exactly.
 */
function finalizeMixedDimensions(
  tokenRaw: readonly RawCostDimension[],
  unitParts: readonly TokenSurchargePart[],
  totalMicros: number,
): UsageCostBreakdownEntry[] {
  const keptToken = tokenRaw.filter((part) => part.tokens > 0 || part.raw_micros > 0);
  const allocated = allocateRemainders(
    [...keptToken.map((part) => part.raw_micros), ...unitParts.map((part) => part.raw_micros)],
    totalMicros,
  );
  const tokenEntries: UsageCostBreakdownEntry[] = keptToken.map((part, index) => {
    const usdMicros = allocated[index] ?? 0;
    return {
      dimension: part.dimension,
      tokens: part.tokens,
      per_mtok_usd: part.per_mtok_usd,
      multiplier: part.multiplier,
      usd_micros: usdMicros,
      usd_cents: usdMicrosToCentsFloor(usdMicros),
    };
  });
  const unitEntries: UsageCostBreakdownEntry[] = unitParts.map((part, index) => {
    const usdMicros = allocated[keptToken.length + index] ?? 0;
    return {
      dimension: 'unit' as const,
      unit: part.unit,
      quantity: part.quantity,
      variant_key: part.variant_key,
      rate_usd_micros_per_unit: part.rate_usd_micros_per_unit,
      usd_micros: usdMicros,
      usd_cents: usdMicrosToCentsFloor(usdMicros),
    };
  });
  return [...tokenEntries, ...unitEntries];
}

/**
 * Resolve the unit price row: exact (unit, variant_key) first, then the
 * (unit, '') default row, else `null` for the missing-price marker.
 *
 * A row that MATCHES but carries an unusable price (zero, negative,
 * fractional, non-finite) resolves to `null` rather than falling through to
 * the default row: a present-but-broken price is a configuration defect, and
 * silently billing the default rate would hide it from quarantine.
 *
 * Duplicate rows for the same (unit, variant_key) resolve first-wins, in
 * array order — deterministic, and pinned by test as the contract.
 *
 * Non-object rows (a stray JSON `null` in a persisted rate list) are dropped
 * before matching rather than dereferenced: today the rates come off SQL
 * columns, but the moment they ride a JSON column a poisoned row must read
 * as "no price" for quarantine, never as a thrown TypeError.
 *
 * ── The exact-match rule (#1878 round two) is DECIDED one level up ─────────
 *
 * ADR-012 §3 makes a declared `variant_key` template forbid the `(unit, '')`
 * default row, so an unpriced tier quarantines as `unit_price_missing`
 * instead of billing the default rate. That rule needs the SPEC, and this
 * function still does not see one: it takes `MeteredPricingRates` plus an
 * already-resolved {@link UnitRateLookupMode}, so the price table and the
 * money policy stay separable and this function stays a pure table lookup.
 *
 * `computeUnitCostBreakdown` is where the decision is made, because that is
 * where the profile and the resolved pricing are both in hand. Platform gets
 * the verdict from its OWN anchor — `readEventTimePricing`'s generation query
 * (`apps/platform-api/src/rating/event-time-pricing.ts`) already
 * `LEFT JOIN`s `model_metering_profiles` on the full
 * `(vendor, model, protocol, enabled = 1)` predicate, so selecting
 * `mp.billable_dimensions_json` there is one more column on an existing join:
 * purely additive, no migration. (NOT `readUnitRatesForPairs` — that one
 * reads `model_unit_pricing` and its tuple carries no protocol, so the
 * profile row cannot be joined onto it.)
 *
 * The verdict must NOT arrive by way of `UnitUsageDetails`. That block is the
 * gateway's persisted claim about one event; widening it to carry pricing
 * policy would make a stored row's rating depend on what the gateway believed
 * at settle time, and platform re-resolves the profile from its own anchor on
 * every rating precisely so that it does not.
 *
 * NOTE the asymmetry with the `variant_key` the block carries: this function
 * is NOT allowed to infer the mode from `variantKey !== ''`, even though that
 * inference happens to be sound for blocks the evaluator authored. It is an
 * inference about the GATEWAY's output, and the whole point of resolving the
 * spec platform-side is that the rating of a stored row does not turn on what
 * the gateway wrote into it.
 */
function lookupUnitRate(
  pricing: MeteredPricingRates,
  unit: UnitUsageUnit,
  variantKey: string,
  mode: UnitRateLookupMode,
): number | null {
  const rows = (pricing.unit_rates ?? []).filter(
    (candidate) => candidate !== null && typeof candidate === 'object',
  );
  const exact = rows.find(
    (candidate) => candidate.unit === unit && candidate.variant_key === variantKey,
  );
  // `exact_match_only` refuses the SECOND rung of the ladder and nothing else.
  // The exact row is still the exact row, and a spec with no template resolves
  // `variant_key` to `''`, so under either mode such a charge reads the
  // `(unit, '')` row by exact match — which is why this rule is invisible to
  // every SKU that bills today.
  const row =
    exact ??
    (mode === 'exact_match_only'
      ? undefined
      : rows.find((candidate) => candidate.unit === unit && candidate.variant_key === ''));
  if (row === undefined) return null;
  const rate = row.rate_usd_micros_per_unit;
  return Number.isSafeInteger(rate) && rate > 0 ? rate : null;
}

function hasPositivePrice(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/**
 * Image-input rate, or the text input rate when the generation has not set
 * one. 0 is an explicit free rate and is returned as 0; only null/undefined/
 * non-finite fall back. That is the compatibility rule #2004 requires: a
 * stock SKU with NULL `image_input_per_mtok_usd` keeps today's cents.
 */
function configuredImageInputRate(pricing: MeteredPricingRates): number {
  const configured = pricing.image_input_per_mtok_usd;
  if (typeof configured === 'number' && Number.isFinite(configured) && configured >= 0) {
    return configured;
  }
  return pricing.input_per_mtok_usd;
}

function safeRoundMicros(rawMicros: number): number | null {
  if (!Number.isFinite(rawMicros)) return null;
  if (rawMicros <= 0) return 0;
  const rounded = Math.floor(rawMicros + 0.5);
  return Number.isSafeInteger(rounded) ? rounded : null;
}

interface RawCostDimension {
  dimension: string;
  tokens: number;
  per_mtok_usd: number;
  multiplier: number;
  raw_micros: number;
}

/**
 * The CLOSED set of token dimension names {@link rawDimension} may write
 * into `UsageCostBreakdown.dimensions`.
 *
 * These are VENDOR spellings, kept as such because the breakdown is the
 * forensic record of what was charged and the dotted Anthropic names are
 * how that vendor's usage document spells them. They are deliberately NOT
 * the tenant-facing vocabulary — `UsageRequestTokenBillingDimension` in
 * `public-usage.ts` is the normalized, lane-neutral union a console keys
 * its labels off, and platform-api's request-log projector maps this set
 * onto that one with an EXHAUSTIVE record.
 *
 * That is the only reason this is a union rather than `string`: adding a
 * tenth dimension here without giving it a normalized counterpart is a
 * typecheck error at the projector, instead of a row that silently loses
 * its itemisation on a tenant's invoice reconciliation.
 */
export type TokenCostDimensionName =
  | 'input_tokens'
  | 'output_tokens'
  | 'cache_read_input_tokens'
  | 'cache_creation_input_tokens'
  | 'cache_creation.other_input_tokens'
  | 'cache_creation.ephemeral_5m_input_tokens'
  | 'cache_creation.ephemeral_1h_input_tokens'
  | 'reasoning_tokens'
  | 'image_input_tokens';

function rawDimension(
  name: TokenCostDimensionName,
  tokens: number,
  perMtokUsd: number,
  multiplier: number,
): RawCostDimension {
  const validTokens = Number.isSafeInteger(tokens) && tokens >= 0;
  const safeTokens = validTokens && tokens > 0 ? tokens : 0;
  const safeRate = Number.isFinite(perMtokUsd) && perMtokUsd > 0 ? perMtokUsd : 0;
  const safeMultiplier = multiplier;
  return {
    dimension: name,
    tokens: safeTokens,
    per_mtok_usd: safeRate,
    multiplier: safeMultiplier,
    raw_micros: validTokens
      ? safeTokens * safeRate * safeMultiplier
      : Number.POSITIVE_INFINITY,
  };
}

function finalizeDimensions(
  raw: readonly RawCostDimension[],
  totalMicros: number,
): UsageCostBreakdownDimension[] {
  const kept = raw.filter((part) => part.tokens > 0 || part.raw_micros > 0);
  const allocated = allocateRemainders(kept.map((part) => part.raw_micros), totalMicros);
  return kept.map((part, index) => {
    const usdMicros = allocated[index] ?? 0;
    return {
      dimension: part.dimension,
      tokens: part.tokens,
      per_mtok_usd: part.per_mtok_usd,
      multiplier: part.multiplier,
      usd_micros: usdMicros,
      usd_cents: usdMicrosToCentsFloor(usdMicros),
    };
  });
}

function buildLegacyBreakdown(params: {
  pricing: MeteredPricingRates;
  profile: MeteringProfileKind;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  rateMultiplier: number;
  missing: readonly string[];
  usageDetails: UsageDetails | null;
  /**
   * Where surcharge `unit_rates` are read from. Distinct from `pricing`
   * when a prompt-length tier or variant replaced the token rates.
   */
  surchargePricing: MeteredPricingRates;
  /** Tier already resolved by the caller; `pricing` carries the tier rates. */
  tierMinPromptTokens: number;
  tierPromptTokens: number;
}): UsageCostBreakdown {
  const {
    pricing,
    profile,
    inputTokens,
    outputTokens,
    cacheReadTokens,
    cacheWriteTokens,
    rateMultiplier,
    missing,
  } = params;
  const openAiCacheWriteFallback =
    (profile === 'openai_responses_v2' ||
    profile === 'openai_chat_v1' ||
    profile === 'openai_images_v1') &&
    missing.includes('cache_creation_input_tokens');
  // OpenAI input_tokens includes cache-read/write subdimensions.  When a
  // cache-write rate is absent or zero, retain write tokens in ordinary input
  // at the configured input price and emit an anomaly.  Never remove those
  // tokens from input and then price them at zero.
  const fallbackInputTokens = openAiCacheWriteFallback
    ? Math.max(0, inputTokens - cacheReadTokens)
    : inputTokens;
  const rawDimensions = [
    rawDimension('input_tokens', fallbackInputTokens, pricing.input_per_mtok_usd, rateMultiplier),
    rawDimension('output_tokens', outputTokens, pricing.output_per_mtok_usd, rateMultiplier),
    rawDimension('cache_read_input_tokens', cacheReadTokens, pricing.cache_read_per_mtok_usd, rateMultiplier),
    ...(!openAiCacheWriteFallback
      ? [rawDimension('cache_creation_input_tokens', cacheWriteTokens, pricing.cache_write_per_mtok_usd, rateMultiplier)]
      : []),
  ];
  return finishTokenCharge({
    pricing: params.surchargePricing,
    profile,
    rateMultiplier,
    rawDimensions,
    tokenUnpriced: missing,
    fallbackToLegacy: missing.length > 0,
    usageDetails: params.usageDetails,
    tierMinPromptTokens: params.tierMinPromptTokens,
    tierPromptTokens: params.tierPromptTokens,
  }).breakdown;
}

/**
 * A zero charge that says why. `unpriced_dimensions` carries the reason, so a
 * zero in the ledger can be told apart from a request that genuinely cost
 * nothing.
 */
function buildZeroBreakdown(
  profile: MeteringProfileKind,
  rateMultiplier: number,
  reason:
    | 'cost_overflow'
    | 'invalid_usage'
    | 'image_not_produced'
    | 'unit_not_delivered'
    | 'unit_price_missing'
    /**
     * The TOKEN-lane twin of `unit_price_missing` (#1937): the SKU declares
     * price tiers and this request's tier has no row in the resolved
     * generation. A DISTINCT marker rather than a fall-through to the base
     * row, so rating quarantines it instead of billing a delivered request at
     * a rate sized for a differently-shaped one.
     *
     * Reaching it means something already slipped past the route gate, which
     * checks the published price identity before dialling. It is depth, and
     * the depth is worth having: the gate reads a projection that can lag, and
     * a lag that ends in a loud $0 is recoverable where a quiet mispriced
     * charge is not.
     */
    | 'variant_price_missing',
): UsageCostBreakdown {
  return {
    schema_version: 1,
    profile,
    rate_multiplier: rateMultiplier,
    cost_usd_micros: 0,
    cost_usd_cents: 0,
    fallback_to_legacy_4_bucket: false,
    unpriced_dimensions: [reason],
    dimensions: [],
    pricing_tier_min_prompt_tokens: 0,
    pricing_tier_prompt_tokens: 0,
  };
}

function buildCostOverflowBreakdown(
  profile: MeteringProfileKind,
  rateMultiplier: number,
): UsageCostBreakdown {
  return buildZeroBreakdown(profile, rateMultiplier, 'cost_overflow');
}

function buildInvalidUsageBreakdown(
  profile: MeteringProfileKind,
  rateMultiplier: number,
): UsageCostBreakdown {
  return buildZeroBreakdown(profile, rateMultiplier, 'invalid_usage');
}
